/**
 * The CMS data table.
 *
 * One component provides search, faceted filters, column sorting, pagination, row selection and
 * bulk actions for every content type, which is why every list screen behaves identically.
 *
 * Accessibility notes: sortable headers are buttons with `aria-sort`, the select-all checkbox
 * reports an indeterminate state, and the row count is announced through a live region so keyboard
 * users hear the result of a filter change.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, EmptyState, Input, Pagination, Select, Skeleton } from '@/components/ui';
import { cn } from '@/lib/cn';

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  align?: 'left' | 'right';
  width?: string;
  render: (row: T) => ReactNode;
}

export interface FilterOption {
  key: string;
  label: string;
  options: { value: string; label: string }[];
}

export interface BulkAction {
  value: string;
  label: string;
  destructive?: boolean;
  disabled?: boolean;
}

interface DataTableProps<T extends { id: number }> {
  caption: string;
  columns: Column<T>[];
  rows: T[];
  count: number;
  page: number;
  pages: number;
  isLoading?: boolean;
  search?: string;
  searchPlaceholder?: string;
  filters?: FilterOption[];
  filterValues?: Record<string, string | undefined>;
  ordering?: string;
  bulkActions?: BulkAction[];
  emptyTitle?: string;
  emptyDescription?: string;
  toolbar?: ReactNode;
  onSearch?: (value: string) => void;
  onFilter?: (key: string, value: string) => void;
  onSort?: (key: string) => void;
  onPage?: (page: number) => void;
  onBulkAction?: (action: string, ids: number[]) => Promise<void> | void;
  rowHref?: (row: T) => string;
}

export function DataTable<T extends { id: number }>({
  caption,
  columns,
  rows,
  count,
  page,
  pages,
  isLoading,
  search,
  searchPlaceholder = 'Search…',
  filters = [],
  filterValues = {},
  ordering,
  bulkActions = [],
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  toolbar,
  onSearch,
  onFilter,
  onSort,
  onPage,
  onBulkAction,
}: DataTableProps<T>) {
  const [selected, setSelected] = useState<number[]>([]);
  const [pendingAction, setPendingAction] = useState('');
  const [busy, setBusy] = useState(false);
  const selectAllRef = useRef<HTMLInputElement>(null);

  // Clear the selection whenever the visible rows change: acting on stale ids would be dangerous.
  useEffect(() => setSelected([]), [rows]);

  const allVisibleSelected = rows.length > 0 && rows.every((row) => selected.includes(row.id));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selected.length > 0 && !allVisibleSelected;
    }
  }, [selected.length, allVisibleSelected]);

  function toggleRow(id: number) {
    setSelected((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  }

  function toggleAll() {
    setSelected(allVisibleSelected ? [] : rows.map((row) => row.id));
  }

  async function runBulkAction() {
    if (!pendingAction || selected.length === 0 || !onBulkAction) return;
    const action = bulkActions.find((item) => item.value === pendingAction);
    if (action?.destructive) {
      const confirmed = window.confirm(
        `${action.label} ${selected.length} record${selected.length === 1 ? '' : 's'}? This cannot be undone.`,
      );
      if (!confirmed) return;
    }
    setBusy(true);
    try {
      await onBulkAction(pendingAction, selected);
      setSelected([]);
      setPendingAction('');
    } finally {
      setBusy(false);
    }
  }

  const sortKey = ordering?.replace(/^-/, '');
  const sortDirection = ordering?.startsWith('-') ? 'descending' : 'ascending';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        {onSearch && (
          <div className="min-w-52 flex-1">
            <label htmlFor="table-search" className="sr-only">
              {searchPlaceholder}
            </label>
            <Input
              id="table-search"
              type="search"
              value={search ?? ''}
              placeholder={searchPlaceholder}
              onChange={(event) => onSearch(event.target.value)}
            />
          </div>
        )}

        {filters.map((filter) => (
          <div key={filter.key}>
            <label htmlFor={`filter-${filter.key}`} className="mb-1 block text-xs font-medium text-muted">
              {filter.label}
            </label>
            <Select
              id={`filter-${filter.key}`}
              value={filterValues[filter.key] ?? ''}
              onChange={(event) => onFilter?.(filter.key, event.target.value)}
              className="min-w-36"
            >
              {filter.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
        ))}

        {toolbar}
      </div>

      {bulkActions.length > 0 && selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-primary/30 bg-primary-soft px-4 py-3">
          <p className="text-sm font-medium text-ink" role="status">
            {selected.length} selected
          </p>
          <label htmlFor="bulk-action" className="sr-only">
            Bulk action
          </label>
          <Select id="bulk-action" value={pendingAction} onChange={(event) => setPendingAction(event.target.value)} className="min-w-44">
            <option value="">Choose an action…</option>
            {bulkActions.map((action) => (
              <option key={action.value} value={action.value} disabled={action.disabled}>
                {action.label}
              </option>
            ))}
          </Select>
          <Button size="sm" onClick={runBulkAction} disabled={!pendingAction} loading={busy}>
            Apply
          </Button>
          <button type="button" onClick={() => setSelected([])} className="text-sm font-medium text-primary underline">
            Clear selection
          </button>
        </div>
      )}

      <p className="text-sm text-muted" role="status" aria-live="polite">
        {isLoading ? 'Loading…' : `${count} record${count === 1 ? '' : 's'}`}
      </p>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-12" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title={emptyTitle} {...(emptyDescription ? { description: emptyDescription } : {})} />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-line bg-surface">
          <table className="w-full border-collapse text-left text-sm">
            <caption className="sr-only">{caption}</caption>
            <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-muted">
              <tr>
                {bulkActions.length > 0 && (
                  <th scope="col" className="w-10 px-4 py-3">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleAll}
                      aria-label="Select all rows on this page"
                      className="h-4 w-4 rounded border-line-strong text-primary"
                    />
                  </th>
                )}
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    style={column.width ? { width: column.width } : undefined}
                    aria-sort={column.sortable && sortKey === column.key ? sortDirection : undefined}
                    className={cn('px-4 py-3 font-semibold', column.align === 'right' && 'text-right')}
                  >
                    {column.sortable && onSort ? (
                      <button
                        type="button"
                        onClick={() => onSort(column.key)}
                        className="inline-flex items-center gap-1 uppercase hover:text-ink"
                      >
                        {column.header}
                        <span aria-hidden="true" className="text-[0.625rem]">
                          {sortKey === column.key ? (sortDirection === 'ascending' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((row) => (
                <tr key={row.id} className={cn('transition-colors hover:bg-raised', selected.includes(row.id) && 'bg-primary-soft/40')}>
                  {bulkActions.length > 0 && (
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.includes(row.id)}
                        onChange={() => toggleRow(row.id)}
                        aria-label={`Select row ${row.id}`}
                        className="h-4 w-4 rounded border-line-strong text-primary"
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td key={column.key} className={cn('px-4 py-3 align-middle', column.align === 'right' && 'text-right')}>
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {onPage && <Pagination page={page} pages={pages} onChange={onPage} className="pt-2" />}
    </div>
  );
}
